import React from 'react';
import {
    Alert,
    Button,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import TimesheetPhaseMaterialInput from './TimesheetPhaseMaterialInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import ContractPhaseQuantityDisplay from './ContractPhaseQuantityDisplay';
import {
    addClientTimesheetContractPhaseAdjustmentSafe,
    isNoQuantityAllowed,
} from './CrewTimesheet';
import { roundToAtMost2Decimals } from '../utilities/NumberHelpers';
import AdjustmentDetail from './AdjustmentDetail';

export default class TimesheetContractPhaseModal extends React.Component {
    getChangeInNotes(underlyingTimesheetContractPhase, entityInQuestion) {
        if (
            !underlyingTimesheetContractPhase ||
            !underlyingTimesheetContractPhase.notes ||
            underlyingTimesheetContractPhase.notes == entityInQuestion.notes
        ) {
            return null;
        }

        return (
            <div className="info">
                <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                Notes from the original timesheet:{' '}
                <pre>{underlyingTimesheetContractPhase.notes}</pre>
            </div>
        );
    }

    onTimesheetContractPhaseQuantityChange = (e) => {
        if (!e) {
            return;
        }

        const { timesheet, job, isAdmin, onTimesheetMutated, contractPhase } =
            this.props;

        const quantity = e.target.valueAsNumber;

        if (isAdmin) {
            addClientTimesheetContractPhaseAdjustmentSafe(
                timesheet,
                job,
                contractPhase.id
            );

            timesheet.timesheetContractPhaseAdjustments.forEach((tcpa) => {
                if (tcpa.contractPhaseId != contractPhase.id) {
                    return;
                }

                tcpa.quantity = quantity;
            });
        } else {
            timesheet.timesheetContractPhases.forEach((tcp) => {
                if (tcp.contractPhaseId != contractPhase.id) {
                    return;
                }

                tcp.quantity = quantity;
            });
        }

        onTimesheetMutated(timesheet);
    };

    recalculateContractPhaseMaterialQuantities = (
        timesheet,
        job,
        contractPhaseId
    ) => {
        timesheet.timesheetContractPhases.forEach((tcp) => {
            if (tcp.contractPhaseId != contractPhaseId) {
                return;
            }

            tcp.timesheetPhaseMaterials.forEach((tpm) => {
                const contractPhase = job.contract.contractPhases.find(
                    (cp) => tcp.contractPhaseId == cp.id
                );

                const phaseMaterial = contractPhase.phaseMaterials.find(
                    (pm) => pm.id == tpm.phaseMaterialId
                );

                const representedPercentage =
                    tcp.quantity / contractPhase.quantity;

                const expectedMaterialConsumption =
                    phaseMaterial.quantity * representedPercentage;

                tpm.quantity = roundToAtMost2Decimals(
                    expectedMaterialConsumption
                );
            });
        });
    };

    recalculateContractPhaseMaterialAdjustmentQuantities = (
        timesheet,
        job,
        contractPhaseId
    ) => {
        timesheet.timesheetContractPhaseAdjustments.forEach((tcpa) => {
            if (tcpa.contractPhaseId != contractPhaseId) {
                return;
            }

            tcpa.timesheetPhaseMaterialAdjustments.forEach((tpma) => {
                const contractPhase = job.contract.contractPhases.find(
                    (cp) => tcpa.contractPhaseId == cp.id
                );

                const phaseMaterial = contractPhase.phaseMaterials.find(
                    (pm) => pm.id == tpma.phaseMaterialId
                );

                const representedPercentage =
                    tcpa.quantity / contractPhase.quantity;

                const expectedMaterialConsumption =
                    phaseMaterial.quantity * representedPercentage;

                tpma.quantity = roundToAtMost2Decimals(
                    expectedMaterialConsumption
                );
            });
        });
    };

    onRecalculateContractPhaseMaterialQuantitiesClick = (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { contractPhaseId } = dataContainer;

        const { timesheet, job, onTimesheetMutated, isAdmin } = this.props;

        if (isAdmin) {
            this.recalculateContractPhaseMaterialAdjustmentQuantities(
                timesheet,
                job,
                contractPhaseId
            );
        } else {
            this.recalculateContractPhaseMaterialQuantities(
                timesheet,
                job,
                contractPhaseId
            );
        }

        onTimesheetMutated(timesheet);
    };

    onTimesheetContractPhaseNotesChange = (dataContainer, e) => {
        const { timesheet, isAdmin, job, onTimesheetMutated } = this.props;

        if (!dataContainer || !e || !e.target) {
            return;
        }

        const { contractPhaseId } = dataContainer;

        if (isAdmin) {
            addClientTimesheetContractPhaseAdjustmentSafe(
                timesheet,
                job,
                contractPhaseId
            );

            timesheet.timesheetContractPhaseAdjustments.forEach((tcpa) => {
                if (tcpa.contractPhaseId != contractPhaseId) {
                    return;
                }

                tcpa.notes = e.target.value;
            });
        } else {
            timesheet.timesheetContractPhases.forEach((tcp) => {
                if (tcp.contractPhaseId != contractPhaseId) {
                    return;
                }

                tcp.notes = e.target.value;
            });
        }

        onTimesheetMutated(timesheet);
    };

    getUnderlyingTimesheetContractPhase() {
        const { timesheet, timesheetContractPhaseAdjustment } = this.props;

        if (
            !timesheetContractPhaseAdjustment ||
            !timesheetContractPhaseAdjustment.timesheetContractPhaseId
        ) {
            return null;
        }

        return timesheet.timesheetContractPhases.find(
            (tcp) =>
                tcp.id ==
                timesheetContractPhaseAdjustment.timesheetContractPhaseId
        );
    }

    render() {
        const {
            loading,
            timesheet,
            timesheetContractPhase,
            timesheetContractPhaseAdjustment,
            isReadOnly,
            isAdmin,
            showModal,
            contractPhase,
            unitOfMeasure,
            entityInQuestion,
            onToggleModal,
        } = this.props;

        if (
            loading ||
            !timesheet ||
            (!timesheetContractPhase && !timesheetContractPhaseAdjustment)
        ) {
            return null;
        }

        const materialsPresent = Boolean(
            (timesheetContractPhaseAdjustment &&
                timesheetContractPhaseAdjustment
                    .timesheetPhaseMaterialAdjustments.length > 0) ||
            (timesheetContractPhase &&
                timesheetContractPhase.timesheetPhaseMaterials.length > 0)
        );

        const underlyingTimesheetContractPhase =
            this.getUnderlyingTimesheetContractPhase();

        const noQuantityAllowed = isNoQuantityAllowed(timesheetContractPhase);

        return (
            <Modal
                isOpen={showModal}
                toggle={onToggleModal}
                fade={false}
                autoFocus={!isAdmin}
                size="xl"
            >
                <ModalHeader toggle={onToggleModal}>
                    {contractPhase.displayPhase} - {contractPhase.description}
                </ModalHeader>
                <ModalBody>
                    {isAdmin && entityInQuestion.isFieldUserAdded && (
                        <Alert color="info" fade={false}>
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className="mr-1"
                            />
                            This contract phase was added by a field user.
                        </Alert>
                    )}

                    <AdjustmentDetail
                        entity={entityInQuestion}
                        {...this.props}
                    />

                    <ContractPhaseQuantityDisplay
                        unitOfMeasure={unitOfMeasure}
                        contractPhase={contractPhase}
                        timesheetContractPhase={entityInQuestion}
                    />

                    <InputGroup className="mt-2" hidden={noQuantityAllowed}>
                        <Input
                            type="number"
                            inputMode="decimal"
                            value={entityInQuestion.quantity}
                            onChange={
                                this.onTimesheetContractPhaseQuantityChange
                            }
                            autoFocus={!isAdmin}
                            disabled={isReadOnly}
                        />
                        <InputGroupText>{unitOfMeasure}</InputGroupText>
                    </InputGroup>

                    {underlyingTimesheetContractPhase &&
                        underlyingTimesheetContractPhase.quantity !=
                            entityInQuestion.quantity && (
                            <div className="info">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="mr-1"
                                />
                                The original timesheet specified{' '}
                                {underlyingTimesheetContractPhase.quantity}{' '}
                                {unitOfMeasure}.
                            </div>
                        )}

                    {materialsPresent && !noQuantityAllowed && (
                        <div className="mt-2">
                            <Button
                                type="button"
                                color="warning"
                                className="w-100"
                                outline={true}
                                onClick={this.onRecalculateContractPhaseMaterialQuantitiesClick.bind(
                                    this,
                                    {
                                        contractPhaseId: contractPhase.id,
                                    }
                                )}
                                hidden={isReadOnly}
                            >
                                <FontAwesomeIcon
                                    icon={faCalculator}
                                    className="mr-2"
                                />
                                Recalculate Material Quantities
                            </Button>
                        </div>
                    )}

                    <InputGroup className="mt-2">
                        <InputGroupText>Notes</InputGroupText>
                        <Input
                            type="textarea"
                            value={entityInQuestion.notes}
                            max={500}
                            maxLength={500}
                            onChange={this.onTimesheetContractPhaseNotesChange.bind(
                                this,
                                {
                                    contractPhaseId: contractPhase.id,
                                    isAdjustment: Boolean(
                                        timesheetContractPhaseAdjustment
                                    ),
                                }
                            )}
                            disabled={isReadOnly}
                        />
                    </InputGroup>

                    {this.getChangeInNotes(
                        underlyingTimesheetContractPhase,
                        entityInQuestion
                    )}

                    <hr />

                    {!noQuantityAllowed &&
                        !timesheetContractPhaseAdjustment &&
                        timesheetContractPhase &&
                        timesheetContractPhase.timesheetPhaseMaterials.map(
                            (tpm) => (
                                <div
                                    key={`tpmi-${timesheetContractPhase.id}-${tpm.phaseMaterialId}`}
                                >
                                    <TimesheetPhaseMaterialInput
                                        timesheetPhaseMaterial={tpm}
                                        {...this.props}
                                    />
                                </div>
                            )
                        )}

                    {!noQuantityAllowed &&
                        timesheetContractPhaseAdjustment &&
                        timesheetContractPhaseAdjustment.timesheetPhaseMaterialAdjustments.map(
                            (tpma) => (
                                <div
                                    key={`tpmai-${timesheetContractPhaseAdjustment.id}-${tpma.phaseMaterialId}`}
                                >
                                    <TimesheetPhaseMaterialInput
                                        timesheetPhaseMaterialAdjustment={tpma}
                                        {...this.props}
                                        underlyingTimesheetContractPhase={
                                            underlyingTimesheetContractPhase
                                        }
                                    />
                                </div>
                            )
                        )}
                </ModalBody>

                <ModalFooter hidden={isReadOnly}>
                    <Button
                        type="button"
                        disabled={entityInQuestion.quantity < 0}
                        color={
                            entityInQuestion.quantity > 0 ? 'success' : 'danger'
                        }
                        onClick={onToggleModal}
                    >
                        Update
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}
